import React, { useState } from "react";
import { useSnackbar } from "notistack";

import { SiteAggregate } from "../../../dtos/siteAggregate";
import { ActiveStatus } from "../../../enums/ActiveStatus";
import { AjaxResult } from "../../../enums/ajaxResult";
import { useAuth } from "../../../features/auth/context";
import { useConfirmDialog } from "../../../hooks/useConfirmDialog";
import { checkIfUserCanActivate } from "../../../utils/activation";
import { useApi } from "../../useApi";
import { navLinkUrl } from "../../../utils";
import { RowActionsMenu, RowAction } from "../../shared/RowActionsMenu";

interface Props {
    site: SiteAggregate;
    sitesAreLoading: boolean;
    contextDealerId: number | undefined;
    contextOrgId: number | undefined;
    onDeleteSite: (id: number) => void;
    onLoadSites: () => void;
    showConfigure: boolean;
    showDelete: boolean;
    showActivation: boolean;
}

/**
 * Site-row actions split between an inline `Configure` link and an
 * `Actions ▾` menu for destructive ops.
 *
 * The Configure link stays at top level so the e2e Playwright tests can
 * find it with `a:has-text('Configure')`. Delete + Activate / Deactivate
 * sit inside the menu and still require `useConfirmDialog` confirmation.
 */
export function SiteRowActions({
    site,
    sitesAreLoading,
    contextDealerId,
    contextOrgId,
    onDeleteSite,
    onLoadSites,
    showConfigure,
    showDelete,
    showActivation,
}: Props) {
    const { siteApi } = useApi();
    const { enqueueSnackbar } = useSnackbar();
    const { showConfirmDialog } = useConfirmDialog();
    const auth = useAuth();
    const [isLoading, setIsLoading] = useState(false);

    const isUsersDealerDeactivated = auth.dealerId !== null && !auth.isDealerActive;
    const isUsersOrgDeactivated = auth.orgId !== null && !auth.isOrgActive;
    const busy = isLoading || sitesAreLoading || isUsersDealerDeactivated || isUsersOrgDeactivated;

    const isActive = site.activeStatus === ActiveStatus.Active;
    const userCanActivate = checkIfUserCanActivate(
        site.activeStatus,
        auth.isDealerAdmin,
        auth.isGlobalAdmin,
        auth.isOrgAdmin,
    );

    const deleteSite = () => {
        showConfirmDialog(`Are you sure you want to delete ${site.name} site?`).then((confirmed) => {
            if (!confirmed) return;
            setIsLoading(true);
            siteApi.delete(site.id).then((r) => {
                if (r.result === AjaxResult.Success) {
                    enqueueSnackbar("Site has been archived");
                    onDeleteSite(site.id);
                } else {
                    enqueueSnackbar("Failed to archive site", { variant: "error" });
                }
                setIsLoading(false);
            });
        });
    };

    const activateSite = () => {
        showConfirmDialog(`Are you sure you want to activate site "${site.name}"?`).then((confirmed) => {
            if (!confirmed) return;
            setIsLoading(true);
            siteApi.activate(site.id).then((r) => {
                if (r.result === AjaxResult.Success) {
                    onLoadSites();
                } else {
                    enqueueSnackbar(`Failed to activate site "${site.name}"`, { variant: "error" });
                }
                setIsLoading(false);
            });
        });
    };

    const deactivateSite = () => {
        showConfirmDialog(`Are you sure you want to deactivate site "${site.name}"?`).then((confirmed) => {
            if (!confirmed) return;
            setIsLoading(true);
            siteApi.deactivate(site.id).then((r) => {
                if (r.result === AjaxResult.Success) {
                    onLoadSites();
                } else {
                    enqueueSnackbar(`Failed to deactivate site "${site.name}"`, { variant: "error" });
                }
                setIsLoading(false);
            });
        });
    };

    const menuActions: RowAction[] = [
        {
            label: "Configure",
            to: navLinkUrl("config", contextDealerId, contextOrgId, site.id),
            hidden: !showConfigure,
        },
        {
            label: isActive ? "Deactivate" : "Activate",
            onClick: isActive ? deactivateSite : activateSite,
            dangerous: isActive,
            disabled: busy || (!isActive && !userCanActivate),
            hidden: !showActivation,
            dividerBefore: true,
        },
        {
            label: "Delete",
            onClick: deleteSite,
            dangerous: true,
            disabled: busy,
            hidden: !showDelete,
        },
    ];

    return <RowActionsMenu actions={menuActions} ariaLabel={`Actions for ${site.name}`} />;
}
